﻿import React, { useCallback, useEffect, useState } from 'react'
import { Button, Input, Text, Select, LoadingSpinner } from '../../components/ui'
import { supabase } from '../../lib/supabase'
import {
  emailDispatchesService,
  extractStorageErrorMessage,
  resolveSentHtmlObjectPath,
} from '../../services/emailDispatches'
import {
  EMAIL_DISPATCH_STATUS_LABELS,
  EMAIL_DISPATCH_STATUS_OPTIONS,
  type EmailDispatch,
  type EmailDispatchStatus,
} from '../../types/emailDispatches'
import { EMAIL_SYSTEM_LABELS, EMAIL_SYSTEM_OPTIONS, type EmailSystem } from '../../types/emailTemplates'
import { ArrowDownTrayIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline'

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function statusBadgeClass(status: EmailDispatchStatus): string {
  switch (status) {
    case 'sent':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    case 'failed':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    default:
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
  }
}

function formatJson(data: unknown): string {
  if (data == null) return '—'
  try {
    return JSON.stringify(data, null, 2)
  } catch {
    return String(data)
  }
}

export const EmailDispatchesTab: React.FC = () => {
  const [items, setItems] = useState<EmailDispatch[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [emptyHint, setEmptyHint] = useState<string | null>(null)

  const [filterSystem, setFilterSystem] = useState<EmailSystem | ''>('')
  const [filterStatus, setFilterStatus] = useState<EmailDispatchStatus | ''>('')
  const [filterRecipient, setFilterRecipient] = useState('')
  const [filterSourceId, setFilterSourceId] = useState('')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')

  const [selected, setSelected] = useState<EmailDispatch | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [actionMessage, setActionMessage] = useState<string | null>(null)

  const downloadHtml = async (row: EmailDispatch, e?: React.MouseEvent) => {
    e?.stopPropagation()
    setActionMessage(null)
    setDownloadingId(row.id)
    try {
      await emailDispatchesService.downloadSentHtml(row)
    } catch (err: unknown) {
      setActionMessage(extractStorageErrorMessage(err, 'Erro ao baixar HTML enviado'))
      window.setTimeout(() => setActionMessage(null), 5000)
    } finally {
      setDownloadingId(null)
    }
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setEmptyHint(null)
    try {
      const data = await emailDispatchesService.list({
        system: filterSystem || undefined,
        status: filterStatus || undefined,
        recipient: filterRecipient || undefined,
        sourceId: filterSourceId || undefined,
        dateFrom: filterDateFrom || undefined,
        dateTo: filterDateTo || undefined,
      })
      setItems(data)
      if (data.length === 0) {
        const { data: canShow } = await supabase.rpc('check_user_permission', {
          module_name: 'configuracoes_email',
          permission_type: 'show',
        })
        if (canShow) {
          setEmptyHint(
            'Nenhum registro retornado pela API. A política "Service role can manage email dispatches" não cobre o frontend (authenticated). Execute frontend/sqls/configuracoes_email_07_dispatches_rls.sql no Supabase para criar a política email_dispatches_select.'
          )
        }
      }
    } catch (e: any) {
      const msg = e?.message || e?.details || 'Erro ao carregar envios'
      const code = e?.code ? ` (${e.code})` : ''
      const hint =
        e?.code === '42501' || /permission|permissão|policy|rls/i.test(msg)
          ? ' Verifique permissão show em configuracoes_email ou execute frontend/sqls/configuracoes_email_07_dispatches_rls.sql no Supabase.'
          : ''
      setError(`${msg}${code}${hint}`)
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [filterSystem, filterStatus, filterRecipient, filterSourceId, filterDateFrom, filterDateTo])

  useEffect(() => {
    load()
  }, [load])

  return (
    <>
      <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <div>
            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Sistema</Text>
            <Select value={filterSystem} onChange={(e) => setFilterSystem(e.target.value as EmailSystem | '')}>
              <option value="">Todos</option>
              {EMAIL_SYSTEM_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Status</Text>
            <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as EmailDispatchStatus | '')}>
              <option value="">Todos</option>
              {EMAIL_DISPATCH_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Destinatário</Text>
            <Input
              value={filterRecipient}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterRecipient(e.target.value)}
              placeholder="Buscar e-mail..."
            />
          </div>
          <div>
            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Origem (source_id)</Text>
            <Input
              value={filterSourceId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterSourceId(e.target.value)}
              placeholder="ID da origem..."
            />
          </div>
          <div>
            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">De</Text>
            <Input type="date" value={filterDateFrom} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterDateFrom(e.target.value)} />
          </div>
          <div>
            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Até</Text>
            <Input type="date" value={filterDateTo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterDateTo(e.target.value)} />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <Text size="sm" color="muted">{items.length} envio(s) (últimos 500)</Text>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="text-gray-600 dark:text-gray-300">
            <ArrowPathIcon className={`h-4 w-4 mr-1.5 inline ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg px-4 py-3 text-sm bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300">
          {error}
        </div>
      )}

      {emptyHint && !error && (
        <div className="mb-4 rounded-lg px-4 py-3 text-sm bg-amber-50 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
          {emptyHint}
        </div>
      )}

      {actionMessage && (
        <div className="mb-4 rounded-lg px-4 py-3 text-sm bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300">
          {actionMessage}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/30">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Criado em</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sistema</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Origem</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modelo</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destinatário</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assunto</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nenhum envio encontrado.
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                  onClick={() => setSelected(row)}
                >
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {formatDateTime(row.created_at)}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.status)}`}>
                      {EMAIL_DISPATCH_STATUS_LABELS[row.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {EMAIL_SYSTEM_LABELS[row.system]}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white max-w-[140px] truncate" title={row.source_id}>
                    {row.source_id}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">#{row.template_id}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[200px] truncate" title={row.recipient}>
                    {row.recipient}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[220px] truncate" title={row.subject_rendered}>
                    {row.subject_rendered}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      title="Baixar HTML enviado"
                      disabled={downloadingId === row.id || !resolveSentHtmlObjectPath(row)}
                      onClick={(e) => downloadHtml(row, e)}
                      className="p-1 text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ArrowDownTrayIcon className={`h-4 w-4 ${downloadingId === row.id ? 'animate-pulse' : ''}`} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelected(null)}>
          <div
            className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
                Detalhes do envio
              </Text>
              <button type="button" onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div className="sm:col-span-2">
                <dt className="text-gray-500 dark:text-gray-400">ID</dt>
                <dd className="font-mono text-gray-900 dark:text-white break-all">{selected.id}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Status</dt>
                <dd>
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(selected.status)}`}>
                    {EMAIL_DISPATCH_STATUS_LABELS[selected.status]}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Sistema</dt>
                <dd className="text-gray-900 dark:text-white">{EMAIL_SYSTEM_LABELS[selected.system]}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Origem (source_id)</dt>
                <dd className="font-mono text-gray-900 dark:text-white break-all">{selected.source_id}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Modelo (template_id)</dt>
                <dd className="font-mono text-gray-900 dark:text-white">#{selected.template_id}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500 dark:text-gray-400">Destinatário</dt>
                <dd className="text-gray-900 dark:text-white break-all">{selected.recipient}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500 dark:text-gray-400">Assunto renderizado</dt>
                <dd className="text-gray-900 dark:text-white">{selected.subject_rendered}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Criado em</dt>
                <dd className="text-gray-900 dark:text-white">{formatDateTime(selected.created_at)}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Concluído em</dt>
                <dd className="text-gray-900 dark:text-white">{formatDateTime(selected.completed_at)}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Mailgrid status</dt>
                <dd className="text-gray-900 dark:text-white">{selected.mailgrid_status || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Mailgrid código</dt>
                <dd className="text-gray-900 dark:text-white">{selected.mailgrid_code || '—'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500 dark:text-gray-400">Mailgrid message ID</dt>
                <dd className="font-mono text-gray-900 dark:text-white break-all">{selected.mailgrid_message_id || '—'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500 dark:text-gray-400">HTML (storage path)</dt>
                <dd className="font-mono text-xs text-gray-900 dark:text-white break-all">{selected.html_storage_path}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Bucket enviado</dt>
                <dd className="font-mono text-xs text-gray-900 dark:text-white break-all">{selected.sent_storage_bucket}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Arquivo enviado</dt>
                <dd className="font-mono text-xs text-gray-900 dark:text-white break-all">{selected.sent_file_path}</dd>
              </div>
              {selected.error_message && (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500 dark:text-gray-400">Erro</dt>
                  <dd className="text-red-700 dark:text-red-300 whitespace-pre-wrap">{selected.error_message}</dd>
                </div>
              )}
              <div className="sm:col-span-2">
                <dt className="text-gray-500 dark:text-gray-400 mb-1">Variáveis</dt>
                <dd>
                  <pre className="text-xs bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded p-3 overflow-x-auto text-gray-800 dark:text-gray-200">
                    {formatJson(selected.variables)}
                  </pre>
                </dd>
              </div>
              {selected.mailgrid_response && (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500 dark:text-gray-400 mb-1">Resposta Mailgrid</dt>
                  <dd>
                    <pre className="text-xs bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded p-3 overflow-x-auto text-gray-800 dark:text-gray-200">
                      {formatJson(selected.mailgrid_response)}
                    </pre>
                  </dd>
                </div>
              )}
            </dl>
            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
              <Button
                variant="outline"
                onClick={() => void downloadHtml(selected)}
                disabled={downloadingId === selected.id || !resolveSentHtmlObjectPath(selected)}
              >
                <ArrowDownTrayIcon className={`h-4 w-4 mr-1.5 inline ${downloadingId === selected.id ? 'animate-pulse' : ''}`} />
                {downloadingId === selected.id ? 'Baixando...' : 'Baixar HTML'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
